import React, { Fragment, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Textarea } from "../../../components/Textarea";
import { UpdateEntryDto } from "../../../model/entry/dto/UpdateEntryDto";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { getRenderedFieldByType } from "../utils/getRenderedFieldByType";
import isEmpty from "lodash/isEmpty";
import { useUnsavedChangesWarningModalContext } from "../../campaign/components/unsaved-changes-warning-modal/unsaved-changes-warning-modal-context";
import { getExcludedTableFields } from "../utils/getExcludedTableFields";
import { entryCreateEditDuplicateErrorHandler } from "../utils/entryCreateEditDuplicateErrorHandler";
import { useDrawersContext } from "../../tasks/drawers-context";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { TestIDs } from "../../../tests/types/testIds";
import { formSliceSelectors } from "../../../redux/forms/form.selector";

// Input fades a disabled field to 20% and paints it mid grey, which leaves
// this read-only value unreadable.
const SourceInput = styled(Input)`
  && {
    opacity: 1 !important;
    background-color: ${({ theme }) => theme.sub} !important;
    color: ${({ theme }) => theme.textColor};
    -webkit-text-fill-color: ${({ theme }) => theme.textColor};
  }
`;

export const EntryUpdateDrawer: React.FC = () => {
  const [entryUpdateLodaing, setEntryUpdateLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openUnsavedChangesModal } = useUnsavedChangesWarningModalContext();
  const { control, formState, getValues, reset, handleSubmit, setError } =
    useForm<UpdateEntryDto>();
  const {
    entry,
    setDuplicateEntries,
    setDuplicateEntryVisible,
    entryUpdateVisible,
    setEntryUpdateVisible,
    setCurrentOnIgnoreAction,
    setDuplicateEntriesGroups,
  } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  // The date field's own config comes from entry.parentForm?.inputs below -
  // its booking map has to come from that same form, not from whatever form
  // happens to be selected elsewhere in the UI (a different form entirely
  // when this entry belongs to one the user isn't currently viewing).
  const entryForm = useAppSelector(formSliceSelectors.selectForms)?.find(
    (form) => form._id === entry?.parentId,
  );

  const sortedFieldKeys = useMemo(() => {
    return tableSettings?.columns
      ?.map((col) => col.field)
      .filter((field) => !getExcludedTableFields().includes(field));
  }, [tableSettings]);

  const handleWarningModalConfirm = () => {
    reset();
    setEntryUpdateVisible(false);
  };

  const localOnClose = () => {
    if (!isEmpty(formState.dirtyFields)) {
      openUnsavedChangesModal(handleWarningModalConfirm);
      return;
    }

    handleWarningModalConfirm();
  };

  const onUpdateEntry = async () => {
    const dto = getValues();

    const { entryId, userId, kind, description, ...rest } = dto;

    const updateDto: UpdateEntryDto = {
      userId,
      entryId: entry?._id!,
      fields: rest.fields,
      description,
    };

    try {
      setEntryUpdateLoading(true);

      await dispatch(entryActions.updateEntryById(updateDto)).unwrap();
      await dispatch(
        entryActions.getEntryById({ entryId: entry?._id!, userId: userId }),
      ).unwrap();

      setEntryUpdateVisible(false);
      AtiraToast.success(t("entries.update.success"));
    } catch (e: any) {
      console.log(e);

      entryCreateEditDuplicateErrorHandler(
        { error: e, keepCurrentModalOpen: true },
        {
          setModal: setDuplicateEntryVisible,
          setGroups: setDuplicateEntriesGroups,
          setActionModal: setEntryUpdateVisible,
          setEntries: setDuplicateEntries,
        },
      );
      setCurrentOnIgnoreAction(() => async () => {
        await dispatch(
          entryActions.updateEntryById({
            ...updateDto,
            ignoreDuplicates: true,
          }),
        );
      });
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setEntryUpdateLoading(false);
    }
  };

  useEffect(() => {
    if (entryUpdateVisible && entry) {
      reset({
        entryId: entry._id || "",
        userId,
        kind: entry.kind,
        description: entry.description,
        fields: entry.fields,
      });
    }
  }, [userId, entry, entryUpdateVisible, reset]);

  if (!entry) {
    return null;
  }

  const firstCustomFieldKey = sortedFieldKeys?.find(
    (f) => tableSettings?.columns?.find((col) => col.field === f)?.custom,
  );

  const sourceField = (
    <Flex flexDirection="column" gap="s">
      <Text fontSize="m">{t("common.source")}</Text>
      <SourceInput
        value={
          entry.origin === "direct" ? t("common.direct") : entry.origin ?? ""
        }
        disabled
        dataTestId={TestIDs.entries.table.menu.edit.drawer.field("source")}
      />
    </Flex>
  );

  return (
    <Drawer
      title={`${t("entries.modal.update.title")} - ${entry?.fields?.name || entry?.fields?.email}`}
      open={entryUpdateVisible}
      onClose={localOnClose}
      destroyOnHidden
      maskClosable={false}
      size="large"
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onUpdateEntry)}
          onCancel={localOnClose}
          submitTestId={TestIDs.entries.table.menu.edit.drawer.button}
          config={{
            loading: entryUpdateLodaing,
            editMode: true,
            disabled: !formState.isValid,
          }}
        />
      }
      key={entry._id}
    >
      <Flex flexDirection="column" justifyContent="center" gap="m">
        {sortedFieldKeys?.map((f) => {
          const formInput = entry.parentForm?.inputs?.find(
            (input) => input.name === f,
          );
          // Edits save the description on the entry itself, which allows more
          // than the form's own description input does
          const maxChar =
            f === "description"
              ? Lengths.VERY_LONG
              : formInput?.settings?.maxChar || Lengths.DESCRIPTION;
          const input =
            formInput && f === "description"
              ? { ...formInput, settings: { ...formInput.settings, maxChar } }
              : formInput;

          return (
            <Fragment key={f}>
              {f === firstCustomFieldKey ? sourceField : null}
              <Controller
                name={f === "description" ? "description" : `fields.${f}`}
                control={control}
                rules={{
                  required: {
                    value: input?.settings?.required === true,
                    message: t("common.this_field_required"),
                  },
                  maxLength: maxChar,
                }}
                render={({ field, fieldState }) => {
                  const currentField = tableSettings?.columns?.find(
                    (col) => col.field === f,
                  );

                  // eslint-disable-next-line no-nested-ternary
                  const translatedLabel = currentField?.custom
                    ? currentField.label
                    : t(`common.${f}`, { defaultValue: input?.label });

                  // eslint-disable-next-line no-nested-ternary
                  return input ? (
                    getRenderedFieldByType(
                      input,
                      field,
                      fieldState,
                      translatedLabel,
                      undefined,
                      { possibleBookedSlots: entryForm?._possibleBookedSlots },
                    )
                  ) : f === "description" ? (
                    <Textarea
                      title={translatedLabel || f}
                      value={field.value}
                      onChange={field.onChange}
                      valid={!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      dataTestId={TestIDs.entries.table.menu.edit.drawer.field(
                        f,
                      )}
                    />
                  ) : (
                    <Input
                      title={translatedLabel || f}
                      value={field.value}
                      onChange={field.onChange}
                      valid={!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      dataTestId={TestIDs.entries.table.menu.edit.drawer.field(
                        f,
                      )}
                    />
                  );
                }}
              />
            </Fragment>
          );
        })}

        {firstCustomFieldKey ? null : sourceField}
      </Flex>
    </Drawer>
  );
};
